import { t } from "@lingui/macro";
import cx from "classnames";
import { ReactNode, useState } from "react";

import { useBreakpoints } from "lib/useBreakpoints";
import { EarnRecommendationToken, sendEarnRecommendationClickedEvent } from "lib/userAnalytics/earnEvents";

import Badge from "components/Badge/Badge";
import Button from "components/Button/Button";
import ExternalLink from "components/ExternalLink/ExternalLink";
import TokenIcon from "components/TokenIcon/TokenIcon";

import ChevronDownIcon from "img/ic_chevron_down.svg?react";

type EarnProductType = "gmx" | "glv" | "gm";

type ProductCopy = {
  tokenSymbol: string;
  badge: ReactNode;
  title: ReactNode;
  description: ReactNode;
  readMoreUrl: string;
  bullets: ReactNode[];
  cta: {
    label: ReactNode;
    to: string;
  };
};

const getCardContent = (type: EarnProductType) => {
  const cardContentByType: Record<EarnProductType, ProductCopy> = {
    gmx: {
      tokenSymbol: "GMX",
      badge: t`Governance`,
      title: t`What is GMX?`,
      description: t`The GMX token serves as the governance token of the GMX protocol. 27% of platform revenue accumulates in the Treasury for GMX buybacks. Stakers earn rewards based on staking power (duration × amount), distributed when GMX reaches $90.`,
      readMoreUrl: "https://docs.gmx.io/docs/tokenomics/gmx-token/",
      bullets: [
        t`Accumulate GMX rewards by staking`,
        t`27% of protocol fees fund Treasury buybacks — rewards distributed at $90`,
        t`Receive voting power and participate in governance`,
      ],
      cta: {
        label: t`Buy GMX`,
        to: "/buy_gmx",
      },
    },
    glv: {
      tokenSymbol: "GLV",
      badge: t`Passive management`,
      title: t`What is GLV?`,
      description: t`GLV (GMX Liquidity Vault) holds various GM tokens, giving you diversified exposure to GMX's markets. The vault auto-rebalances to high-demand markets, unlocking higher potential yield compared to isolated pools.`,
      readMoreUrl: "https://docs.gmx.io/docs/providing-liquidity/#glv-pools",
      bullets: [
        t`Get optimized exposure to the highest-utilized pools`,
        t`No active management required`,
        t`Earn fees from leverage trading, swaps, and spreads`,
        t`All earnings auto-compound`,
        t`Great for passive users`,
      ],
      cta: {
        label: t`Buy GLV`,
        to: "/pools",
      },
    },
    gm: {
      tokenSymbol: "GM",
      badge: t`Active management`,
      title: t`What is GM?`,
      description: t`GM tokens represent shares in a liquidity pool for a single GMX market. Buy GM to provide liquidity and earn fees from leverage trading, swaps, and market-making activity on that specific market. All rewards are auto-compounded.`,
      readMoreUrl: "https://docs.gmx.io/docs/providing-liquidity/#gm-pools",
      bullets: [
        t`Provide isolated liquidity per market`,
        t`Become a market maker`,
        t`Requires more active management than GLV`,
        t`All earnings auto-compound`,
      ],
      cta: {
        label: t`Buy GM`,
        to: "/pools",
      },
    },
  };
  return cardContentByType[type];
};

const BULLET_GRADIENT_STYLE = {
  background: "linear-gradient(180deg, #03D1CFFC 10%, #4E09F8 140%)",
};

export default function EarnProductCard({
  type,
  className,
  openBuyGmxModal,
}: {
  type: EarnProductType;
  className?: string;
  openBuyGmxModal: () => void;
}) {
  const content = getCardContent(type);
  const { isMobile } = useBreakpoints();
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldCollapse = isMobile && !isExpanded;

  const handleRecommendationClick = () => {
    sendEarnRecommendationClickedEvent({
      activeTab: "discover",
      context: "AboutTokens",
      token: content.tokenSymbol as EarnRecommendationToken,
    });

    if (type === "gmx") {
      openBuyGmxModal();
    }
  };

  return (
    <div
      className={cx(
        "relative flex h-full flex-col gap-16 rounded-8 bg-slate-900 p-20 transition-[max-height] duration-300 ease-in-out",
        className
      )}
    >
      <div className={cx("relative flex flex-col gap-16", { "max-h-[136px] overflow-hidden": shouldCollapse })}>
        <div className="flex items-start justify-between gap-12">
          <TokenIcon symbol={content.tokenSymbol} displaySize={40} className="!rounded-0" />
          <Badge className="!bg-fill-accent">{content.badge}</Badge>
        </div>

        <div className="flex flex-col gap-12">
          <h3 className="text-body-large font-medium text-typography-primary">{content.title}</h3>
          <p className="text-body-medium text-typography-secondary">{content.description}</p>
          <ExternalLink href={content.readMoreUrl} variant="icon" className="text-body-small font-medium">
            {t`Read more`}
          </ExternalLink>
        </div>

        <div className="border-t-1/2 border-t-slate-600" />

        <div className="text-body-medium flex flex-col gap-8 text-typography-secondary">
          {content.bullets.map((bullet, index) => (
            <div key={index} className="flex items-center gap-8">
              <span className="size-8 shrink-0 rotate-[45deg]" style={BULLET_GRADIENT_STYLE} />
              <span className="text-body-small font-medium text-typography-primary">{bullet}</span>
            </div>
          ))}
        </div>

        {shouldCollapse && (
          <div className="to-transparent pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-900" />
        )}
      </div>

      <div className="mt-auto flex gap-8">
        {isMobile && (
          <Button
            variant="secondary"
            className="flex-1 justify-center"
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
          >
            <span className="flex items-center justify-center gap-8">
              {isExpanded ? t`Less info` : t`More info`}
              <ChevronDownIcon className={cx("size-16 transition-transform", { "rotate-180": isExpanded })} />
            </span>
          </Button>
        )}
        <Button
          variant="primary"
          className="flex-1 justify-center"
          to={type !== "gmx" ? content.cta.to : undefined}
          onClick={handleRecommendationClick}
        >
          {content.cta.label}
        </Button>
      </div>
    </div>
  );
}
